import { ReactElement, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FileScanEpisode,
  FileScanRun,
  LinkCandidate,
  linkEpisodeToSession,
  linkRunToServerRun,
} from '../../api/episodeDbScan';
import { apiErrorDetail } from '../../utils/apiError';
import { configShortLabel } from '../../utils/configRole';
import { destinationSessions, sessionOptionLabel } from '../../utils/sessionConfigChoice';

/**
 * The two links the recordings page makes without building anything.
 *
 * A recording is linked to a session: that is how the runs of the session find
 * the recording's data (its BLE wind frames, above all) when they are
 * reprocessed. A recording the phone never registered is claimed for the team
 * by linking it: by anyone in the team whose login uploaded its file, and by an
 * admin for a file uploaded before the server stamped the uploader.
 *
 * A phone run is linked to a run the server already has: a device zip carries
 * no phone run id, so the page reports that run missing, and an import would
 * build it a second time.
 */

const selectClass =
  'cr-min-w-0 cr-max-w-full cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs';
const linkButtonClass =
  'cr-rounded cr-border-none cr-bg-blue-900 cr-px-3 cr-py-1 cr-text-xs cr-font-semibold cr-text-white ' +
  'hover:cr-bg-blue-850 disabled:cr-cursor-not-allowed disabled:cr-bg-cirrusBlue-100 disabled:cr-opacity-60';
const quietButtonClass =
  'cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium ' +
  'cr-text-gray-700 hover:cr-bg-gray-50 disabled:cr-cursor-not-allowed disabled:cr-opacity-50';

/** The session most of the episode's runs already sit in, or would be linked into. */
function suggestedSessionId(episode: FileScanEpisode): string | null {
  const counts = new Map<string, number>();
  episode.runs.forEach((run) => {
    const sessionId = run.session_id ?? run.link_candidates?.[0]?.session_id;
    if (sessionId) counts.set(sessionId, (counts.get(sessionId) ?? 0) + 1);
  });
  let best: string | null = null;
  counts.forEach((count, sessionId) => {
    if (best === null || count > (counts.get(best) ?? 0)) best = sessionId;
  });
  return best ?? episode.testsession_id;
}

interface EpisodeSessionLinkProps {
  episode: FileScanEpisode;
  sessions?: any[] | null;
  disabled?: boolean;
  onLinked: () => Promise<unknown> | void;
}

export function EpisodeSessionLink({ episode, sessions, disabled, onLinked }: EpisodeSessionLinkProps): ReactElement {
  const targets = useMemo(() => destinationSessions(sessions), [sessions]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const claiming = episode.ownership === 'unregistered';
  const current = episode.testsession_id ? targets.find((s: any) => s.id === episode.testsession_id) : null;
  const sessionId = picked ?? suggestedSessionId(episode) ?? '';

  const link = async () => {
    if (!sessionId || !episode.data_file) return;
    setBusy(true);
    try {
      const result = await linkEpisodeToSession(episode.data_file.s3_key, episode.episode_id, sessionId);
      const what = result.claimed ? 'Claimed for your team and linked' : 'Linked';
      toast.success(
        `${what} to ${result.session_description}. The recording's data is being stored now; ` +
          'reprocess the session’s runs once it is in.',
      );
      setOpen(false);
      setPicked(null);
      await onLinked();
    } catch (err) {
      toast.error(`Could not link the recording: ${apiErrorDetail(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2 cr-pl-6 cr-text-left cr-text-xs cr-text-gray-600">
      <span>
        {episode.testsession_id
          ? `Recording linked to ${current ? sessionOptionLabel(current) : 'a session'}`
          : 'Recording not linked to a session'}
      </span>
      {!open && (
        <button type="button" className={quietButtonClass} disabled={disabled} onClick={() => setOpen(true)}>
          {claiming ? 'Claim and link to a session…' : 'Link to a session…'}
        </button>
      )}
      {open && (
        <>
          <select
            aria-label="Session to link the recording to"
            className={selectClass}
            value={sessionId}
            disabled={busy || disabled}
            onChange={(e) => setPicked(e.target.value)}
          >
            <option value="">Select a Session</option>
            {targets.map((s: any) => (
              <option key={s.id} value={s.id}>
                {sessionOptionLabel(s)}
              </option>
            ))}
          </select>
          <button type="button" className={linkButtonClass} disabled={!sessionId || busy || disabled} onClick={link}>
            {busy ? 'Linking…' : 'Link'}
          </button>
          <button type="button" className={quietButtonClass} disabled={busy} onClick={() => setOpen(false)}>
            Cancel
          </button>
        </>
      )}
    </div>
  );
}

function candidateLabel(candidate: LinkCandidate): string {
  const where = candidate.session_description
    ? `${candidate.session_description} / ${configShortLabel(candidate.config_letter_number)}`
    : 'not in a session';
  const offset = `${candidate.offset_s >= 0 ? '+' : ''}${Math.round(candidate.offset_s)} s`;
  return `${candidate.description} — ${where} (${offset})`;
}

interface RunServerLinkProps {
  episode: FileScanEpisode;
  run: FileScanRun;
  disabled?: boolean;
  onLinked: () => Promise<unknown> | void;
}

/** "Same run as …": for a run reported missing that a zip run starts beside. */
export function RunServerLink({ episode, run, disabled, onLinked }: RunServerLinkProps): ReactElement | null {
  const candidates = run.link_candidates ?? [];
  const [picked, setPicked] = useState<string>(candidates[0]?.rundata_id ?? '');
  const [busy, setBusy] = useState(false);
  if (run.on_server || candidates.length === 0) return null;
  if (episode.ownership !== 'mine') {
    return (
      <div className="cr-pl-9 cr-text-left cr-text-xs cr-text-gray-500">
        The server has a run that starts beside this one — link the recording to a session first to match them.
      </div>
    );
  }

  const link = async () => {
    if (!picked || !episode.data_file) return;
    setBusy(true);
    try {
      await linkRunToServerRun(episode.data_file.s3_key, episode.episode_id, run.run_uuid, picked);
      toast.success('Linked to the run already on the server');
      await onLinked();
    } catch (err) {
      toast.error(`Could not link the run: ${apiErrorDetail(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2 cr-pl-9 cr-text-left cr-text-xs cr-text-gray-600">
      <span>Same run as</span>
      <select
        aria-label="Server run that is this run"
        className={selectClass}
        value={picked}
        disabled={busy || disabled}
        onChange={(e) => setPicked(e.target.value)}
      >
        {candidates.map((candidate) => (
          <option key={candidate.rundata_id} value={candidate.rundata_id}>
            {candidateLabel(candidate)}
          </option>
        ))}
      </select>
      <button type="button" className={linkButtonClass} disabled={!picked || busy || disabled} onClick={link}>
        {busy ? 'Linking…' : 'Link'}
      </button>
    </div>
  );
}
